import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Avatar } from "@mui/material";
import UrlApi from "src/utils/Url";
import { initials, isBlank } from "./format";

// Photos that failed to load, remembered in this browser so a missing file is
// asked for once, not on every page, tab and reload. A new photo gets a new
// file name, so it is still tried. Shared with the top bar's avatar.
const FAILED_KEY = "safrati.cms.failedPhotos";
const memory = new Set();
export const failedPhotos = () => {
  try {
    return [...memory, ...(JSON.parse(window.localStorage.getItem(FAILED_KEY)) || [])];
  } catch {
    return [...memory];
  }
};
export const rememberFailedPhoto = (src) => {
  memory.add(src);
  try {
    window.localStorage.setItem(FAILED_KEY, JSON.stringify([...new Set([...failedPhotos(), src])].slice(-100)));
  } catch {
    /* blocked storage: remembered until reload */
  }
};

// The API's stand-in picture ("defaultImage-….png") is a drawing of a broken
// image: treat it as no photo.
const PLACEHOLDER = /(^|\/)default[-_ ]?image/i;

// No file name: "uploads/admins/", "uploads/admins/null". The API sends the
// folder alone when a person has no photo, and asking for it is a 404.
const NO_FILE = /(^|\/)(null|undefined)?$/i;

export function photoUrl(path) {
  if (isBlank(path)) return undefined;
  const value = String(path).trim().split(/[?#]/)[0];
  if (PLACEHOLDER.test(value) || NO_FILE.test(value)) return undefined;
  if (/^https?:\/\/[^/]+\/?$/.test(value)) return undefined;
  return /^https?:\/\//.test(value) ? value : UrlApi.baseUrlImage + value;
}

// A person's photo, or their initials on a neutral disc: never a broken
// image. Decorative by default (the name is next to it); pass `label` when
// the avatar stands alone.
const PersonAvatar = ({ name, src, size = 36, label, sx }) => {
  const url = photoUrl(src);
  const [broken, setBroken] = useState(() => !!url && failedPhotos().includes(url));
  useEffect(() => setBroken(!!url && failedPhotos().includes(url)), [url]);
  const show = url && !broken ? url : undefined;
  return (
    <Avatar
      src={show}
      alt={label || ""}
      aria-hidden={label ? undefined : true}
      imgProps={{
        loading: "lazy",
        onError: () => {
          rememberFailedPhoto(url);
          setBroken(true);
        },
      }}
      sx={[
        {
          width: size,
          height: size,
          fontSize: Math.max(11, Math.round(size * 0.38)),
          fontWeight: 700,
          letterSpacing: 0,
          bgcolor: (t) => (t.palette.mode === "dark" ? "#22314A" : "#E4E9F0"),
          color: "text.secondary",
          flexShrink: 0,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {initials(name)}
    </Avatar>
  );
};

PersonAvatar.propTypes = {
  name: PropTypes.string,
  src: PropTypes.string,
  size: PropTypes.number,
  label: PropTypes.string,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
};

export default PersonAvatar;
